import React from "react";
import AvailablePlanCard, { AvailablePlan } from "./AvailablePlanCard";
import {
  useGetSubscriptionPlansQuery,
  useStartSubscriptionMutation,
  useLazyEditSubscriptionQuery,
} from "../../../../services/subscriptionAPI";
import { LoadingSpinner } from "../../../../utils/StateDisplay";
import { PricePeriod, ChangeType, getPlanChangeType } from "../utils";
import { toast } from "react-toastify";

export type OnPlanSelectType = {
  period: PricePeriod;
  price: number;
  priceID: string;
  groupID: string;
  currentPlanName: string;
  selectedPlanName: string;
};

const AvailablePlans: React.FC = () => {
  const {
    data: availableSubscriptions,
    isLoading: isAvailablePlansLoading,
    error,
  } = useGetSubscriptionPlansQuery("");

  const [
    startSubscription,
    {
      isLoading: isStartSubscriptionLoading,
      isSuccess: isStartSubscriptionSuccess,
      isError: isStartSubscriptionError,
      error: startSubscriptionError,
      data: startSubscriptionData,
    },
  ] = useStartSubscriptionMutation();

  const [
    updateSubscription,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      data: updateData,
    },
  ] = useLazyEditSubscriptionQuery();

  const handlePlanSelection = async (planSelection: OnPlanSelectType) => {
    const { period, price, priceID, groupID, currentPlanName, selectedPlanName } =
      planSelection;
    const planChangeType = getPlanChangeType(currentPlanName, selectedPlanName); // [starting, upgrading, downgrading, null]

    const redirectToUrl = (url: string) => {
      if (url && url !== "free") {
        window.location.href = url;
      }
    };

    try {
      let apiResult;

      switch (planChangeType) {
        case ChangeType.Starting:
          apiResult = await startSubscription({
            price_id: priceID,
            group_id: groupID,
          }).unwrap();
          redirectToUrl(apiResult.url);
          break;

        case ChangeType.Upgrading:
        case ChangeType.Downgrading:
          apiResult = await updateSubscription({ group_id: groupID }).unwrap();
          redirectToUrl(apiResult.url);
          break;

        default:
          toast.error("Plan change not allowed. Please select a valid plan.");
          return;
      }
    } catch (error) {
      toast.error(
        "There was an issue updating your subscription. Please try again later."
      );
    }
  };

  if (isAvailablePlansLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <p>Failed to load plans. Please try again</p>;
  }

  if (!availableSubscriptions || availableSubscriptions.length === 0) {
    return <p>No subscription plans available at the moment</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-6 w-full">
      {availableSubscriptions.map((plan: AvailablePlan) => (
        <AvailablePlanCard key={plan.id} plan={plan} onPlanSelect={handlePlanSelection} />
      ))}
    </div>
  );
};

export default AvailablePlans;
